/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect, ReactElement } from "react";

import { useForm } from "react-hook-form";

import * as Yup from "yup";

import { Stack, StackItem } from "@fluentui/react";
import { yupResolver } from "@hookform/resolvers/yup";

import TextFieldController from "Components/ReactHookForm/TextFieldController/TextFieldController";
import { SidePanel } from "Components/SidePanel/SidePanel";
import { IM365BenefitDetail } from "Interfaces/Benefits/IM365BenefitDetail";
import { IMicrosoft365BenefitDetail } from "Interfaces/Benefits/IMicrosoft365BenefitDetail";

import { IM365DetailsEditFormProps } from "./IM365DetailsEditFormProps";

import { BenefitNameEnum, BenefitDisplayNameEnum } from "Constants/BenefitConstants";
import { GUID_REG_EX } from "Constants/ValidationMessages";

export const M365DetailsEditForm = (props: IM365DetailsEditFormProps): ReactElement => {
  const {
    isEditM365PanelOpen,
    setIsEditM365PanelOpen,
    m365UpdateErrorMessage,
    dismissPanel,
    updateM365BenefitDetail,
    m365BenefitDetail,
    updateMS365BenefitDetail,
    benefitType,
  } = props;

  const isSaving = false;

  // handle Yup validation dynamically as subscriptionId is not available for O365.
  let validation = {
    tenantId: Yup.string().optional().required("Tenant Id is required"),
    tenantName: Yup.string().required("Tenant Name is required"),
  };
  if (benefitType === BenefitNameEnum.MS365) {
    const copyObject = Object.assign(validation);
    copyObject.subscriptionId = Yup.string()
      .required("Subscription Id is required")
      .matches(
        GUID_REG_EX,
        "Subscription ID should be in GUID format: [11111111-2222-3333-4444-555555555555]"
      );
    validation = copyObject;
  }

  /**
   * Format subscription Id
   * @param {IMicrosoft365BenefitDetail} values MS365 details
   * @returns {void}
   */
  const FormatSubscriptionId = (values: IMicrosoft365BenefitDetail) => {
    // set SubscriptionId from 11111111222233334444555555555555 to 11111111-2222-3333-4444-555555555555
    if (values.subscriptionId.length < 36) {
      const hyphenNumbers = [8, 13, 18, 23];
      hyphenNumbers.forEach((item) => {
        if (values.subscriptionId.slice(item, item + 1) != "-") {
          values.subscriptionId =
            values.subscriptionId.slice(0, item) + "-" + values.subscriptionId.slice(item);
        }
      });
    }
  };

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(
      Yup.object().shape({
        ...validation,
      })
    ),
  });

  const defaultValues:
    | IM365BenefitDetail
    | IMicrosoft365BenefitDetail
    | undefined = m365BenefitDetail;

  useEffect(() => {
    reset({ ...defaultValues });
  }, [defaultValues, reset]);

  const save = () =>
    handleSubmit((data) => {
      updateM365BenefitDetail && updateM365BenefitDetail(data as IM365BenefitDetail);

      benefitType === BenefitNameEnum.MS365 &&
        FormatSubscriptionId(data as IMicrosoft365BenefitDetail);
      updateMS365BenefitDetail && updateMS365BenefitDetail(data as IMicrosoft365BenefitDetail);

      setIsEditM365PanelOpen(false);
    });

  return (
    <form onSubmit={save()}>
      <SidePanel
        isOpen={isEditM365PanelOpen}
        title={`Edit ${BenefitDisplayNameEnum[benefitType]} Details`}
        saveButtonLabel="Save"
        error=""
        disableActions={isSaving}
        onDismiss={dismissPanel}
        onSave={save()}
      >
        <Stack tokens={{ childrenGap: "1rem" }}>
          {benefitType === BenefitNameEnum.MS365 && (
            <StackItem>
              <TextFieldController
                control={control}
                inputName="subscriptionId"
                errors={errors}
                isRequired={true}
                labelDisplayName="Subscription Id:"
                hasTooltip={true}
                toolTipDisplayText={`The Tenant Id of your ${BenefitDisplayNameEnum[benefitType]} Benefit Subscription.`}
                textFieldType="text"
                placeholderText="Enter Subscription Id"
                isDisabled={isSaving}
              />
            </StackItem>
          )}
          <StackItem>
            <TextFieldController
              control={control}
              inputName="tenantId"
              errors={errors}
              isRequired={true}
              labelDisplayName="Tenant Id:"
              hasTooltip={true}
              toolTipDisplayText={`The Tenant Id of your ${BenefitDisplayNameEnum[benefitType]} Benefit Subscription.`}
              textFieldType="text"
              placeholderText="Enter Tenant Id"
              isDisabled={isSaving}
            />
          </StackItem>
          <StackItem>
            <TextFieldController
              control={control}
              inputName="tenantName"
              errors={errors}
              isRequired={true}
              labelDisplayName="Tenant/Domain Name:"
              hasTooltip={true}
              toolTipDisplayText={`The Tenant/Domain name of your ${BenefitDisplayNameEnum[benefitType]} Subscription.`}
              textFieldType="text"
              placeholderText="Enter Tenant/Domain Name"
              isDisabled={isSaving}
            />
          </StackItem>
          <StackItem>
            {m365UpdateErrorMessage && m365UpdateErrorMessage.length > 0 && m365UpdateErrorMessage}
          </StackItem>
        </Stack>
      </SidePanel>
    </form>
  );
};
